import { Injectable, inject } from '@angular/core';
import { DirtyCheckPlugin, Query } from '@datorama/akita';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import {
  LoanDataModel,
  LoanFeaturesModel,
  OriginationChannel,
  ReferralInfoModel,
  RelatedLoanModel,
  MortgageType,
  SectionOfAct,
  FundingFeeType,
  PmiType,
  LoanPurposeType,
  DpFactorType,
  AmortizationType,
  RefinancePurposeVa,
  RefinancePurposeFha,
} from '../../cmg-1003/models';

import { Cmg1003LoanStore } from './loan.store';
import { DirtyChecker2 } from '../core/dirty-checker-2';
import { CA885PaymentScenariosModel } from '../../disclosure/models/loan-application.model';
import { FeatureFlagRepository, FeatureName } from '../../core/state';

@Injectable({ providedIn: 'root' })
export class Cmg1003LoanQuery extends Query<LoanDataModel> {
  protected store: Cmg1003LoanStore;
  protected featureFlagRepo = inject(FeatureFlagRepository);

  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);
  loanPurposeDirtyChecker = new DirtyCheckPlugin(this, { watchProperty: 'loanPurpose' });
  referralInfoDirtyChecker = new DirtyCheckPlugin(this, { watchProperty: 'referralInfo' });

  constructor() {
    const store = inject(Cmg1003LoanStore);

    super(store);
    this.store = store;

    this.setHeadAllDirtyCheckers();
  }

  resetAllDirtyCheckers() {
    this.dirtyChecker2.dirtyChecker.reset();
    this.loanPurposeDirtyChecker.reset();
    this.referralInfoDirtyChecker.reset();
  }

  setHeadAllDirtyCheckers() {
    this.dirtyChecker2.dirtyChecker.setHead();
    this.loanPurposeDirtyChecker.setHead();
    this.referralInfoDirtyChecker.setHead();
  }

  selectLoanFeatures(): Observable<LoanFeaturesModel> {
    return this.selectLoanInfo().pipe(
      switchMap((state) => {
        const loanFeatures = {
          isBalloon: state.isBalloon,
          isInterestOnly: state.isInterestOnly,
          isNegativeAmortization: state.isNegativeAmortization,
          isPrepaymentPenalty: state.isPrepaymentPenalty,
          hasPrepaymentPenalty: state.hasPrepaymentPenalty,
          prepaymentPenaltyMax: state.prepaymentPenaltyMax,
          prepaymentPenaltyTerm: state.prepaymentPenaltyTerm,
          isTemporaryBuydown: state.isTemporaryBuydown,
          isOther: state.isOther,
          productDescription: state.productDescription,
          productDescriptionOther: state.productDescriptionOther,
          isRateReboundRefinance: state.isRateReboundRefinance,
          qualRateReboundLoanNumber: state.qualRateReboundLoanNumber,
          cemaLoan: state.cemaLoan,
          cemaPreviousCmgLoanNumber: state.cemaPreviousCmgLoanNumber,
          cemaInternalCollateralReqDate: state.cemaInternalCollateralReqDate,
          cemaCollateralReqFromCustodianDate: state.cemaCollateralReqFromCustodianDate,
          extendedRateLock: state.extendedRateLock,
          halalSFC: state.halalSFC,
        } as LoanFeaturesModel;
        return of(loanFeatures);
      }),
    );
  }
  selectLoanInfo(): Observable<LoanDataModel> {
    return this.select().pipe(
      //  Only emit when loanId is something
      filter((state) => !!state.loanId),
    );
  }
  selectLoanOriginationChannel(): Observable<OriginationChannel> {
    return this.selectLoanInfo().pipe(map((state) => state.originationChannel));
  }
  selectLoanTotalLoanAmount(): Observable<number> {
    return this.selectLoanInfo().pipe(map((state) => state.totalLoanAmount));
  }
  selectReferralInfo(): Observable<ReferralInfoModel> {
    return this.selectLoanInfo().pipe(map((state) => state.referralInfo));
  }
  selectRelatedLoans(): Observable<RelatedLoanModel[]> {
    return this.selectLoanInfo().pipe(map((state) => state.relatedLoans));
  }
  selectRateReboundRefinance(): Observable<boolean> {
    return this.selectLoanInfo().pipe(map((state) => state.isRateReboundRefinance));
  }
  assembleForSave(): LoanDataModel {
    const loanInfo = this.getValue();
    if (loanInfo?.totalLoanAmount === 0 && loanInfo?.baseLoanAmount > 0) {
      loanInfo.totalLoanAmount = loanInfo.baseLoanAmount;
    }
    return loanInfo;
  }
  showPmiType$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.conventional || loan.mortgageAppliedFor === MortgageType.other;
    }),
  );
  showAfterImpValue$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        loan.mortgageAppliedFor === MortgageType.fha && (loan.sectionOfAct === SectionOfAct._203_k || loan.sectionOfAct === SectionOfAct._221_d_2)
      );
    }),
  );
  isFha$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.fha;
    }),
  );
  isLockedDown$ = combineLatest([
    this.featureFlagRepo.isFeatureEnabled$(FeatureName.ClosedLoansCrossBusinessUnitRestriction),
    this.select((l) => l.isCrossBusinessEntity),
  ]).pipe(map(([isLockdownEnabled, isCrossBusinessEntity]) => isLockdownEnabled && isCrossBusinessEntity));
  isVa$ = this.select((t) => t.mortgageAppliedFor).pipe(map((t) => t === MortgageType.va));

  isFhaRefi$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.fha && loan.loanPurpose === LoanPurposeType.refinance;
    }),
  );
  isFundingFee$ = this.selectLoanInfo().pipe(
    distinctUntilChanged((oldLoan, newLoan) => oldLoan.mortgageAppliedFor === newLoan.mortgageAppliedFor),
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.va;
    }),
  );
  isUSDA$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.usda;
    }),
  );
  isOther$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.other;
    }),
  );
  isConventional$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.conventional;
    }),
  );
  isHeloc$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.heloc;
    }),
  );
  isAioNonConstruction$ = this.selectLoanInfo().pipe(
    map((loan) => {
      const code = loan?.loanProgramCode?.toLocaleLowerCase();
      return code?.startsWith('801') && !code?.includes('1xc');
    }),
  );
  ltv$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.ltv;
    }),
  );
  mortgageType$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor;
    }),
  );
  upfrontMiFactor$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.upfrontMiFactor;
    }),
  );
  miMonthlyPrem$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.monthlyMiAmount;
    }),
  );
  miPFFFinancingCalcOption$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mipffFinancingCalcOption;
    }),
  );
  mipffPercent$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mipffPercent;
    }),
  );
  loanProgramCode$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanProgramCode;
    }),
  );

  showMonthlyMI$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        (loan.mortgageAppliedFor === MortgageType.va && loan.fundingFeeType !== FundingFeeType.exempt && !!loan.fundingFeeType) ||
        (loan.mortgageAppliedFor === MortgageType.fha && !!loan.caseNumberAssignedDate) ||
        loan.mortgageAppliedFor === MortgageType.usda ||
        (loan.pmiType !== PmiType.none &&
          loan.pmiType !== PmiType.select &&
          loan.pmiType !== PmiType.lenderPaid &&
          (loan.mortgageAppliedFor === MortgageType.conventional || loan.mortgageAppliedFor === MortgageType.other)) ||
        loan.mortgageAppliedFor === MortgageType.heloc
      );
    }),
  );
  showMonthlyMIPaidInCash$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        (loan.mortgageAppliedFor === MortgageType.va && loan.fundingFeeType !== FundingFeeType.exempt && !!loan.fundingFeeType) ||
        (loan.mortgageAppliedFor === MortgageType.fha && !!loan.caseNumberAssignedDate) ||
        loan.mortgageAppliedFor === MortgageType.usda ||
        (loan.pmiType !== PmiType.none &&
          loan.pmiType !== PmiType.select &&
          loan.pmiType !== PmiType.lenderPaid &&
          loan.pmiType !== PmiType.monthly && //Only difference from showMonthlyMIPaidInCash() & showMonthlyMI()
          (loan.mortgageAppliedFor === MortgageType.conventional || loan.mortgageAppliedFor === MortgageType.other)) ||
        loan.mortgageAppliedFor === MortgageType.heloc
      );
    }),
  );
  showRefinancePurpose$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanPurpose === LoanPurposeType.refinance;
    }),
  );
  showRefinancePurposeCashOut$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        loan.loanPurpose === LoanPurposeType.refinance &&
        ((loan.mortgageAppliedFor === MortgageType.va && loan.refinancePurposeVa === RefinancePurposeVa.Other) ||
          (loan.mortgageAppliedFor === MortgageType.fha && loan.refinancePurposeFha === RefinancePurposeFha.CashOut))
      );
    }),
  );
  showOtherPurposeDescription$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanPurpose === LoanPurposeType.other;
    }),
  );
  showConstructionFields$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanPurpose === LoanPurposeType.construction || loan.loanPurpose === LoanPurposeType.constructionPerm;
    }),
  );
  showRateChange$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.amortizationType === AmortizationType.ARM;
    }),
  );
  hasSoldRealEstateIn60Months$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.haveYouSoldRealEstateIn60Months === true;
    }),
  );
  showDownPaymentPercentage$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.dpfactor === DpFactorType.downpaymentPercentage;
    }),
  );
  showDownPaymentAmount$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.dpfactor === DpFactorType.downpaymentAmount;
    }),
  );
  showAmortizationTypeDescription$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.amortizationType === AmortizationType.ARM || loan.amortizationType === AmortizationType.NoPayment;
    }),
  );
  showQualRateOption$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.amortizationType === AmortizationType.ARM;
    }),
  );
  showHomeOwnedThroughVa$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.areYouDisabledVeteran === false;
    }),
  );
  showAntiSteering$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.originationChannel === OriginationChannel.brokeredOut && loan.mortgageAppliedFor !== MortgageType.heloc;
    }),
  );
  selectLoanPaymentScenarios(): Observable<CA885PaymentScenariosModel> {
    return this.selectLoanInfo().pipe(switchMap((state) => of(state.cA885PaymentScenario)));
  }

  isCemaLoan$ = this.selectLoanInfo().pipe(map((loan) => loan.cemaLoan?.value === 'True' || false));
}
